import { Injectable, inject, signal } from '@angular/core';

import { CamPermissionsService } from '@camelot/user';

import { environment } from '../../environments/environment';

const BYPASS_STORAGE_KEY = 'merlin.bypassPermissions';
const MOCK_STORAGE_KEY = 'merlin.mockPermissions';
const MOCK_FEATURES_STORAGE_KEY = 'merlin.mockFeatures';
const QUERY_PARAM = 'bypassPermissions';

@Injectable({
  providedIn: 'root',
})
export class AppPermissionsBypassService {
  public readonly enabled = signal(false);
  public readonly mockFeatures = signal<string[] | null>(null);
  public readonly mockPermissions = signal<string[] | null>(null);
  public readonly isAvailable = environment.PERMISSIONS_BYPASS;

  private readonly _permissionsService = inject(CamPermissionsService);

  // Bypass is on by default in debug; `?bypassPermissions=0` or the panel turns it off, the storage keeps the choice.
  public init() {
    if (!this.isAvailable) {
      return;
    }
    const storedMock = sessionStorage.getItem(MOCK_STORAGE_KEY);
    if (storedMock !== null) {
      this._applyMock(JSON.parse(storedMock));
    }
    const storedFeatures = sessionStorage.getItem(MOCK_FEATURES_STORAGE_KEY);
    if (storedFeatures !== null) {
      this._applyMockFeatures(JSON.parse(storedFeatures));
    }

    const fromUrl = new URLSearchParams(window.location.search).get(QUERY_PARAM);
    if (fromUrl !== null) {
      this.set(fromUrl === '1' || fromUrl === 'true');
      return;
    }
    const stored = sessionStorage.getItem(BYPASS_STORAGE_KEY);
    if (stored === null) {
      this._apply(true);
      return;
    }
    this.set(stored === '1');
  }

  // Only an explicit choice (panel or URL) is persisted, the default never is.
  public set(enabled: boolean) {
    if (!this.isAvailable) {
      return;
    }
    this._apply(enabled);
    sessionStorage.setItem(BYPASS_STORAGE_KEY, enabled ? '1' : '0');
  }

  // `null` restores the permissions returned by the backend.
  public setMockPermissions(permissions: string[] | null) {
    if (!this.isAvailable) {
      return;
    }
    this._applyMock(permissions);
    this._store(MOCK_STORAGE_KEY, permissions);
  }

  // `null` restores the features returned by the backend.
  public setMockFeatures(features: string[] | null) {
    if (!this.isAvailable) {
      return;
    }
    this._applyMockFeatures(features);
    this._store(MOCK_FEATURES_STORAGE_KEY, features);
  }

  private _apply(enabled: boolean) {
    this.enabled.set(enabled);
    this._permissionsService.setBypass(enabled);
  }

  private _applyMock(permissions: string[] | null) {
    this.mockPermissions.set(permissions);
    this._permissionsService.setMockPermissions(permissions);
  }

  private _applyMockFeatures(features: string[] | null) {
    this.mockFeatures.set(features);
    this._permissionsService.setMockFeatures(features);
  }

  private _store(key: string, value: string[] | null) {
    if (value === null) {
      sessionStorage.removeItem(key);
    } else {
      sessionStorage.setItem(key, JSON.stringify(value));
    }
  }
}
